import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  NumberInput,
  parseNonNegativeInteger,
  parseNonNegativeNumber,
} from '../../../components/ui/NumberInput';
import { AntdDate } from '../../../components/ui/antd-date';
import { Notification } from '../../../components/ui/custom-notification';
import { FileUpload } from '../../../components/ui/FileUpload';
import { MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../constants/fileUpload';
import {
  createWarehouseMaterialTraceability,
  getPreSignedUrlToUploadWarehouseDoc,
  updateWarehouseMaterialTraceability,
} from '../../../redux/Project/materialSaga';
import type { MaterialData } from '../../project/types/materials';
import { DocumentTag } from '../../project/types';
import {
  buildWarehouseCreatePayload,
  buildWarehouseUpdatePayload,
  createEmptyWarehouseSegment,
  emptyWarehousePipeForm,
  mergeWarehouseDocuments,
  resizeWarehouseSegments,
  warehouseFormFromMaterial,
  type WarehousePipeFormState,
  type WarehousePipeSegment,
} from '../utils/warehouseMaterialPayload';
import {
  logPipeInventoryMaterialAdded,
  logPipeInventoryMaterialUpdated,
} from '../utils/pipeInventoryAudit';

const UNIT_OPTIONS: { value: string; label: string }[] = [
  { value: 'in', label: 'Inch' },
  { value: 'cm', label: 'Centimeter' },
  { value: 'mm', label: 'Millimeter' },
  { value: 'ft', label: 'Foot' },
  { value: 'm', label: 'Meter' },
];

type AnalysisWarehouseMaterialDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  material?: MaterialData | null;
  documentTags?: DocumentTag[];
};

export const AnalysisWarehouseMaterialDialog: React.FC<AnalysisWarehouseMaterialDialogProps> = ({
  isOpen,
  onClose,
  onSaved,
  material,
  documentTags = [],
}) => {
  const isEdit = Boolean(material?.materialId);
  const [form, setForm] = useState<WarehousePipeFormState>(emptyWarehousePipeForm);
  const [submitting, setSubmitting] = useState(false);
  const [lengthUnit, setLengthUnit] = useState('in');
  const [odUnit, setOdUnit] = useState('in');
  const [wtUnit, setWtUnit] = useState('in');
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);

  const tagOptions = useMemo(
    () =>
      documentTags
        .filter((tag) => tag.tags_type === 'MATERIAL_PIPE_TAGS')
        .map((tag) => ({
          value: tag.tags_id.toString(),
          label: tag.tags_name,
        })),
    [documentTags]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (material) {
      setForm(warehouseFormFromMaterial(material));
    } else {
      setForm(emptyWarehousePipeForm());
    }
    setMaterialDocuments([]);
    setSelectedTags([]);
  }, [isOpen, material]);

  const patch = (partial: Partial<WarehousePipeFormState>) => {
    setForm((prev) => ({ ...prev, ...partial }));
  };

  const numField = (
    key: 'pipeLength' | 'pipeOuterDiameter' | 'pipeWallThickness',
    value: number | string | ''
  ) => {
    if (value === '' || value == null) {
      patch({ [key]: '' });
      return;
    }
    const str =
      key === 'pipeLength' ? String(parseNonNegativeNumber(String(value))) : String(value);
    patch({ [key]: str });
  };

  const handleNumberOfPupsChange = (value: number | string | '') => {
    const raw = value === '' || value == null ? '' : String(parseNonNegativeInteger(String(value)));
    const count = raw === '' ? 0 : parseNonNegativeInteger(raw);
    setForm((prev) => ({
      ...prev,
      numberOfPups: raw,
      pipeSegmentation: resizeWarehouseSegments(
        prev.pipeSegmentation,
        Number.isFinite(count) ? count : 0,
        prev.materialDate || undefined
      ),
    }));
  };

  const updateSegment = <K extends keyof WarehousePipeSegment>(
    id: string,
    key: K,
    value: WarehousePipeSegment[K]
  ) => {
    setForm((prev) => ({
      ...prev,
      pipeSegmentation: prev.pipeSegmentation.map((seg) =>
        seg.id === id ? { ...seg, [key]: value } : seg
      ),
    }));
  };

  const addSegment = () => {
    setForm((prev) => {
      const next = [
        ...prev.pipeSegmentation,
        createEmptyWarehouseSegment(
          prev.pipeSegmentation.length,
          prev.materialDate || undefined
        ),
      ];
      return {
        ...prev,
        pipeSegmentation: next,
        numberOfPups: String(next.length),
      };
    });
  };

  const removeSegment = (id: string) => {
    setForm((prev) => {
      const next = prev.pipeSegmentation.filter((seg) => seg.id !== id);
      return {
        ...prev,
        pipeSegmentation: next,
        numberOfPups: next.length ? String(next.length) : '',
      };
    });
  };

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (files?.length) {
      setMaterialDocuments((prev) => [...prev, ...Array.from(files)]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments((prev) => prev.filter((_, i) => i !== index));
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags((prev) => {
      const next = [...prev];
      next[fileIndex] = tags;
      return next;
    });
  };

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      setSubmitting(true);
      let payload = isEdit
        ? buildWarehouseUpdatePayload(Number(material!.materialId), form)
        : buildWarehouseCreatePayload(form);

      if (materialDocuments.length > 0) {
        const presignPayload = {
          projectId: null,
          pipeId: null,
          projectTypeId: null,
          materialCategory: 'Pipe',
          materialId: isEdit ? material!.materialId : null,
        };
        const s3Response = await getPreSignedUrlToUploadWarehouseDoc(
          materialDocuments,
          presignPayload,
          1
        );
        if (!s3Response?.length) {
          Notification.warning(
            'Document upload failed',
            'Material was not saved because document upload did not complete.'
          );
          return;
        }
        payload = mergeWarehouseDocuments(
          payload,
          s3Response.map((file: { fileName: string; s3Key: string }, index: number) => ({
            fileName: file.fileName,
            s3Key: file.s3Key,
            version: 1,
            tags: selectedTags[index] || [],
            uploadedBy: user.email || '',
          }))
        );
      }

      const result = isEdit
        ? await updateWarehouseMaterialTraceability(payload)
        : await createWarehouseMaterialTraceability(payload);

      if (result) {
        const auditRow = {
          materialId: isEdit ? material!.materialId : (result as { materialId?: number }).materialId,
          warehouseProjectLabel: form.warehouseProjectLabel,
          details: {
            pipeNRI: form.pipeNRI,
            pipeNo: form.pipeNo,
            pipeHeatNumber: form.pipeHeatNumber,
            pipePipeId: form.pipePipeId,
            warehouseProjectLabel: form.warehouseProjectLabel,
          },
        };
        if (isEdit) {
          logPipeInventoryMaterialUpdated(auditRow);
        } else {
          logPipeInventoryMaterialAdded(auditRow);
        }
        onSaved();
        onClose();
      }
    } catch (e) {
      if (e instanceof Error && e.message === 'PIPE_NO_REQUIRED') {
        Notification.warning('Please fill the required fields', 'Pipe No is required.');
      } else if (e instanceof Error && e.message === 'HEAT_NUMBER_REQUIRED') {
        Notification.warning('Please fill the required fields', 'Heat Number is required.');
      } else {
        Notification.warning('Save failed', 'Could not save material. Check API availability.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-7xl flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Material' : 'Add Material'}</DialogTitle>
        </DialogHeader>

        <div className="p-2">
          <div className="grid p-2 grid-cols-4 gap-4 min-w-0 max-h-[75vh] overflow-y-auto overflow-x-hidden">
            <div className="space-y-2">
              <Label htmlFor="projectName">Project Name</Label>
              <Input
                id="projectName"
                value={form.warehouseProjectLabel}
                onChange={(e) => patch({ warehouseProjectLabel: e.target.value })}
                placeholder="Enter project name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Date</Label>
              <AntdDate
                name="materialDate"
                value={form.materialDate}
                onChange={(value: string) => patch({ materialDate: value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.materialStatus}
                onValueChange={(v) => patch({ materialStatus: v as 'Available' | 'Consumed' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Consumed">Consumed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pipeNo">
                Pipe No <span className="text-red-500">*</span>
              </Label>
              <Input
                id="pipeNo"
                value={form.pipeNo}
                onChange={(e) => patch({ pipeNo: e.target.value })}
                placeholder="Enter Pipe No"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="heatNumber">
                Heat Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="heatNumber"
                value={form.pipeHeatNumber}
                onChange={(e) => patch({ pipeHeatNumber: e.target.value })}
                placeholder="Enter Heat Number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pipeId">Pipe Id</Label>
              <Input
                id="pipeId"
                value={form.pipePipeId}
                onChange={(e) => patch({ pipePipeId: e.target.value })}
                placeholder="Enter Pipe Id"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pipeNRI">Pipe NRI</Label>
              <Input
                id="pipeNRI"
                value={form.pipeNRI}
                onChange={(e) => patch({ pipeNRI: e.target.value })}
                placeholder="Enter Pipe NRI"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="grade">Grade</Label>
              <Input
                id="grade"
                value={form.pipeGradeLabel}
                onChange={(e) => patch({ pipeGradeLabel: e.target.value })}
                placeholder="Enter grade"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="materialType">Material Type</Label>
              <Input
                id="materialType"
                value={form.pipeMaterialTypeLabel}
                onChange={(e) => patch({ pipeMaterialTypeLabel: e.target.value })}
                placeholder="Enter material type"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="condition">Condition</Label>
              <Input
                id="condition"
                value={form.pipeCondition}
                onChange={(e) => patch({ pipeCondition: e.target.value })}
                placeholder="Enter condition"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="manufacturer">Manufacturer</Label>
              <Input
                id="manufacturer"
                value={form.manufacturerLabel}
                onChange={(e) => patch({ manufacturerLabel: e.target.value })}
                placeholder="Enter manufacturer"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wbsNumber">WBS Number</Label>
              <Input
                id="wbsNumber"
                value={form.wbsNumber}
                onChange={(e) => patch({ wbsNumber: e.target.value })}
                placeholder="Enter WBS number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contractor">Contractor</Label>
              <Input
                id="contractor"
                value={form.contractor}
                onChange={(e) => patch({ contractor: e.target.value })}
                placeholder="Enter contractor"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="outerDiameter">Outer Diameter</Label>
              <NumberInput
                id="outerDiameter"
                showUnitSelect
                unitValue={odUnit}
                unitOptions={UNIT_OPTIONS}
                onUnitChange={setOdUnit}
                value={form.pipeOuterDiameter ? Number(form.pipeOuterDiameter) : ''}
                onChange={(value) => numField('pipeOuterDiameter', value)}
                placeholder="Enter Outer Diameter"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wallThickness">Wall Thickness</Label>
              <NumberInput
                id="wallThickness"
                showUnitSelect
                unitValue={wtUnit}
                unitOptions={UNIT_OPTIONS}
                onUnitChange={setWtUnit}
                value={form.pipeWallThickness ? Number(form.pipeWallThickness) : ''}
                onChange={(value) => numField('pipeWallThickness', value)}
                placeholder="Enter Wall Thickness"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lengthReceived">Length</Label>
              <NumberInput
                id="lengthReceived"
                showUnitSelect
                unitValue={lengthUnit}
                unitOptions={UNIT_OPTIONS}
                onUnitChange={setLengthUnit}
                min={0}
                step={0.1}
                wheelAdjust
                maxDecimalPlaces={4}
                value={form.pipeLength ? Number(form.pipeLength) : ''}
                onChange={(value) => numField('pipeLength', value)}
                placeholder="Enter length"
              />
            </div>

            <div className="space-y-2">
              <Label>Project Stage</Label>
              <Select
                value={form.pipeProjectStage || 'Production'}
                onValueChange={(value: 'PQT' | 'Production') => patch({ pipeProjectStage: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="PQT">PQT</SelectItem>
                  <SelectItem value="Production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Date Recorded</Label>
              <AntdDate
                name="dateRecorded"
                value={form.dateRecorded}
                onChange={(value: string) => patch({ dateRecorded: value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="numberOfPups">Number of pups</Label>
              <NumberInput
                id="numberOfPups"
                value={form.numberOfPups ? Number(form.numberOfPups) : ''}
                min={0}
                step={1}
                wheelAdjust
                onChange={handleNumberOfPupsChange}
                placeholder="Enter number of pups"
              />
            </div>

            <div className="space-y-2">
              <Label className="invisible select-none text-sm font-medium" aria-hidden="true">
                MTR received
              </Label>
              <div className="flex h-10 items-center gap-2">
                <Checkbox
                  id="mtrReceived"
                  checked={form.mtrReceived}
                  onCheckedChange={(c) => patch({ mtrReceived: c === true })}
                />
                <Label htmlFor="mtrReceived" className="font-normal cursor-pointer">
                  MTR received
                </Label>
              </div>
            </div>

            {form.pipeSegmentation.length > 0 && (
              <div className="space-y-3 col-span-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium">Pipe Segments</h3>
                  <Button
                    type="button"
                    onClick={addSegment}
                    className="flex items-center gap-2"
                    size="sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Segment
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Segment Name
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Segment Length
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Date Inventory
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Used
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {form.pipeSegmentation.map((segment) => (
                        <tr key={segment.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Input
                              value={segment.segmentName}
                              onChange={(e) =>
                                updateSegment(segment.id, 'segmentName', e.target.value)
                              }
                              className="w-full min-w-[120px]"
                              placeholder="A"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <NumberInput
                              value={segment.segmentLength}
                              min={0}
                              step={0.1}
                              wheelAdjust
                              emptyWhenZero={false}
                              maxDecimalPlaces={4}
                              onChange={(value) =>
                                updateSegment(
                                  segment.id,
                                  'segmentLength',
                                  value === '' ? 0 : parseNonNegativeNumber(value)
                                )
                              }
                              className="w-full min-w-[140px]"
                              placeholder="0.0"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <AntdDate
                              type="date"
                              value={segment.dateInventory || ''}
                              onChange={(date) =>
                                updateSegment(
                                  segment.id,
                                  'dateInventory',
                                  date ? String(date).slice(0, 10) : ''
                                )
                              }
                              className="w-full min-w-[160px]"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={segment.used}
                              onCheckedChange={(checked) =>
                                updateSegment(segment.id, 'used', checked === true)
                              }
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              type="button"
                              onClick={() => removeSegment(segment.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="space-y-2 col-span-4">
              <Label className="text-base font-medium">Marking</Label>
              <textarea
                value={form.pipeMarking}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  patch({ pipeMarking: e.target.value })
                }
                className="w-full mt-2 p-3 border border-gray-300 min-h-[60px] rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-700"
                placeholder="Enter marking..."
              />
            </div>

            <div className="space-y-2 col-span-4">
              <Label className="text-base font-medium">Notes</Label>
              <textarea
                value={form.pipeNotes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  patch({ pipeNotes: e.target.value })
                }
                className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700"
                placeholder="Enter notes..."
              />
            </div>

            <div className="col-span-4 space-y-2">
              <Label>Upload Documents</Label>
              <p className="text-sm text-gray-600">
                Upload material documents (optional).
                <br />
                <span className="text-xs text-gray-500">
                  Allowed: PDF, Word, Excel, Text, Images — max {MAX_UPLOAD_FILE_SIZE_LABEL} per file
                </span>
              </p>
              <FileUpload
                onFileSelect={handleMaterialFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                multiple
                maxFiles={100}
                buttonText="Upload Documents"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveFile}
                selectedFiles={materialDocuments}
                showFileList={materialDocuments.length > 0}
                className="w-full"
                showTags
                optionsTags={tagOptions}
                selectedTags={selectedTags}
                onTagsChange={handleTagsChange}
                maxHeight=""
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3 mt-6 flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !form.pipeNo.trim()}
            className="bg-[#EB1C37] text-white px-4 py-2 rounded-lg hover:bg-[#EB1C37]/90 transition-colors"
            loading={submitting}
            loadingText={isEdit ? 'Saving…' : 'Adding Material...'}
          >
            {isEdit ? 'Save changes' : 'Add Material'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
